import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Badge, HStack, IconButton, Button, useDisclosure, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, Select, VStack, Text,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import ConfirmModal from '../components/ConfirmModal';
import roomService from '../services/roomService';
import roomTypeService from '../services/roomTypeService';
import apiClient from '../services/apiClient';

const STATUS_COLORS = {
  vacant: 'green', occupied: 'blue', maintenance: 'orange', blocked: 'red', dirty: 'yellow',
};

const RoomManagement = () => {
  const { t } = useTranslation();
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ room_number: '', room_type: '', floor: 1, status: 'vacant', cleaning_status: 'clean', is_active: true, display_order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const deleteDisclosure = useDisclosure();
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [r, rt] = await Promise.all([roomService.getAll(), roomTypeService.getAll()]);
      setRooms(r?.results || r || []);
      setRoomTypes(rt?.results || rt || []);
    } catch { setError(t('rooms.errors.loadFailed')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ room_number: '', room_type: roomTypes[0]?.id || '', floor: 1, status: 'vacant', cleaning_status: 'clean', is_active: true, display_order: 0 });
    setEditingId(null); onOpen();
  };

  const openEdit = (room) => {
    setForm({
      room_number: room.number || room.room_number,
      room_type: room.roomTypeId || room.room_type_details?.id || '',
      floor: room.floor || 1,
      status: room.status || 'vacant',
      cleaning_status: room.cleaningStatus || room.cleaning_status || 'clean',
      is_active: true,
      display_order: room.displayOrder ?? room.display_order ?? 0,
    });
    setEditingId(room.id); onOpen();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) await roomService.update(editingId, form);
      else {
        const hotelId = apiClient.getHotelId();
        await roomService.create({ ...form, hotel: hotelId ? Number(hotelId) : undefined });
      }
      toast({ title: editingId ? t('rooms.toasts.updated') : t('rooms.toasts.created'), status: 'success', duration: 2000 });
      onClose(); load();
    } catch (err) {
      toast({ title: t('common.error'), description: JSON.stringify(err.response?.data), status: 'error', duration: 4000 });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await roomService.remove(deleteTarget.id);
      toast({ title: t('rooms.toasts.deleted'), status: 'success', duration: 2000 });
      deleteDisclosure.onClose(); load();
    } catch { toast({ title: t('rooms.toasts.deleteFailed'), status: 'error', duration: 3000 }); }
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <PageShell
      title={t('rooms.title')}
      subtitle={t('rooms.subtitle', { count: rooms.length })}
      breadcrumbs={[{ label: t('rooms.title') }]}
      loading={loading} error={error}
      actions={<Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={openCreate}>{t('rooms.buttons.add')}</Button>}
    >
      <Card shadow="sm" borderWidth="1px">
        <CardBody p={0}>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>{t('rooms.table.roomNumber')}</Th>
                  <Th>{t('rooms.table.type')}</Th>
                  <Th>{t('rooms.fields.floor')}</Th>
                  <Th>{t('rooms.table.sort')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('rooms.table.cleaning')}</Th>
                  <Th>{t('rooms.table.rate')}</Th>
                  <Th>{t('common.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rooms.map(r => (
                  <Tr key={r.id}>
                    <Td fontWeight="600" fontSize="sm">{r.number || r.room_number}</Td>
                    <Td fontSize="sm">{r.type || r.room_type_details?.name}</Td>
                    <Td fontSize="sm">{r.floor}</Td>
                    <Td fontSize="sm">{r.displayOrder ?? r.display_order ?? 0}</Td>
                    <Td>
                      <Badge colorScheme={STATUS_COLORS[r.status] || 'gray'} fontSize="xs">{r.status ? t(`rooms.statuses.${r.status}`, { defaultValue: r.status }) : r.status}</Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={(r.cleaningStatus || r.cleaning_status) === 'clean' ? 'green' : 'yellow'} fontSize="xs">
                        {(r.cleaningStatus || r.cleaning_status)
                          ? t(`rooms.cleaningStatuses.${r.cleaningStatus || r.cleaning_status}`, { defaultValue: r.cleaningStatus || r.cleaning_status })
                          : (r.cleaningStatus || r.cleaning_status)}
                      </Badge>
                    </Td>
                    <Td fontSize="sm">{r.rate || r.room_type_details?.base_rate}</Td>
                    <Td>
                      <HStack spacing={1}>
                        <IconButton size="xs" icon={<EditIcon />} variant="ghost" onClick={() => openEdit(r)} aria-label={t('common.edit')} />
                        <IconButton size="xs" icon={<DeleteIcon />} variant="ghost" colorScheme="red"
                          onClick={() => { setDeleteTarget(r); deleteDisclosure.onOpen(); }} aria-label={t('common.delete')} />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
                {rooms.length === 0 && <Tr><Td colSpan={8} textAlign="center" py={8} color="gray.400">{t('rooms.emptyState')}</Td></Tr>}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? t('rooms.modal.editTitle') : t('rooms.buttons.add')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('rooms.fields.roomNumber')}</FormLabel>
                <Input size="sm" value={form.room_number} onChange={set('room_number')} placeholder="101" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">{t('rooms.fields.roomType')}</FormLabel>
                <Select size="sm" value={form.room_type} onChange={set('room_type')}>
                  {roomTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name} ({rt.code})</option>)}
                </Select>
              </FormControl>
              <HStack w="100%" spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('rooms.fields.floor')}</FormLabel>
                  <Input size="sm" type="number" value={form.floor} onChange={set('floor')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('common.status')}</FormLabel>
                  <Select size="sm" value={form.status} onChange={set('status')}>
                    <option value="vacant">{t('rooms.statuses.vacant')}</option>
                    <option value="occupied">{t('rooms.statuses.occupied')}</option>
                    <option value="maintenance">{t('rooms.statuses.maintenance')}</option>
                    <option value="blocked">{t('rooms.statuses.blocked')}</option>
                  </Select>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm">{t('rooms.fields.sortOrder')}</FormLabel>
                <Input size="sm" type="number" min="0"
                       value={form.display_order}
                       onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })} />
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {t('rooms.fields.sortOrderHelp')}
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>{editingId ? t('common.save') : t('common.create')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal isOpen={deleteDisclosure.isOpen} onClose={deleteDisclosure.onClose}
        onConfirm={handleDelete} title={t('rooms.deleteModal.title')}
        message={t('rooms.deleteModal.message', { room: deleteTarget?.number || deleteTarget?.room_number })} />
    </PageShell>
  );
};

export default RoomManagement;
